import { Component, HostBinding, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Client } from '@app/models/client.model';
import { APPOINTMENT_REQUEST, CUSTOMER_ID, LocalStorageService, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { SiteService } from '@app/services/site.service';
import { UserAccountService } from '@app/services/user-account.service';
import { provideIcons } from '@ng-icons/core';
import { lucideAlertTriangle, lucideLoader } from '@ng-icons/lucide';
import { firstValueFrom } from 'rxjs';
import { SiteBookingSettings } from './../../models/siteBookingSettings.model';

export enum WarningScreenType {
    InvalidUrlScreen,
    ErrorScreen,
    IncorrectTimeScreen,
}

@Component({
    selector: 'app-error',
    templateUrl: './error.component.html',
    styleUrls: ['./error.component.css'],
    standalone: false,
    providers: [provideIcons({ lucideAlertTriangle, lucideLoader })],
})
export class ErrorComponent implements OnInit {
    private invalidURLMainTitle = `Oops, that address doesn't look right.`;
    private invalidURLSubtext = 'Please verify that the url is correct and try again.';
    private errorScreenMainTitle = 'Oops, something went wrong...';
    private errorScreenURLSubtext = 'Please try again or call the number below to schedule an appointment.';
    private incorrectTimeMainTitle = 'Oops, the system time seems to be incorrect.';
    private incorrectTimeSubtext = "Please synchronize your system's time and try again.";

    screenMainTitle = '';
    screenSubtext = '';
    screenType: WarningScreenType | undefined;
    siteSettings: SiteBookingSettings | undefined;
    businessPhoneNumber = '';
    phoneNumberExists = false;
    isErrorScreen = false;
    isLoading = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private localStorageService: LocalStorageService,
        private userAccountService: UserAccountService,
        private siteService: SiteService
    ) {}

    @HostBinding('class.error-container')
    ngOnInit() {
        this.resolveScreenType(this.router.url);
    }

    private resolveScreenType(url: string) {
        const errorMessageToDisplay = this.route.snapshot.queryParams?.['ErrorMessage'];
        this.siteSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.screenMainTitle = this.errorScreenMainTitle;
        this.screenSubtext = this.errorScreenURLSubtext;
        this.isErrorScreen = true;
        this.screenType = WarningScreenType.ErrorScreen;
        if (this.siteSettings?.businessPhoneNumber) {
            this.phoneNumberExists = true;
            this.businessPhoneNumber = this.siteSettings.businessPhoneNumber;
        } else {
            this.screenSubtext = this.screenSubtext.replace('us at: ', 'the business.');
        }

        if (errorMessageToDisplay) {
            this.screenMainTitle = `${errorMessageToDisplay}`;
        } else if (url.toLowerCase().includes('incorrecttime')) {
            this.screenMainTitle = this.incorrectTimeMainTitle;
            this.screenSubtext = this.incorrectTimeSubtext;
            this.isErrorScreen = false;
            this.screenType = WarningScreenType.IncorrectTimeScreen;
        } else if (url.toLowerCase().includes('invalid')) {
            this.screenMainTitle = this.invalidURLMainTitle;
            this.screenSubtext = this.invalidURLSubtext;
            this.isErrorScreen = false;
            this.screenType = WarningScreenType.InvalidUrlScreen;
        }
    }

    public get WarningScreenType() {
        return WarningScreenType;
    }

    public async requestAppointmentAgain(): Promise<void> {
        this.isLoading = true;
        try {
            let client: Client | undefined = undefined;
            if (this.userAccountService.getCurrentUser() !== undefined) {
                client = (this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest)?.client;
            }
            const customerID = this.localStorageService.getString(CUSTOMER_ID);
            this.localStorageService.remove(APPOINTMENT_REQUEST);
            this.localStorageService.remove(SITE_BOOKING_SETTINGS);
            // Fetch fresh site booking settings before navigating
            try {
                const freshSettings = await firstValueFrom(this.siteService.getSiteBookingSettings(customerID));
                this.localStorageService.set(SITE_BOOKING_SETTINGS, freshSettings);
            } catch (error) {
                console.error('Failed to fetch site booking settings:', error);
            }
            if (client) {
                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Client, client);
            }

            void this.router.navigate(['/booking/service'], {
                queryParams: {
                    DSID: customerID,
                },
            });
        } finally {
            this.isLoading = false;
        }
    }
}
